import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Button,
  Avatar,
  Divider,
  useTheme,
  Menu,
  MenuItem,
  Chip,
  LinearProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
} from '@mui/material';
import { Rnd } from 'react-rnd';
import RemoveIcon from '@mui/icons-material/Remove';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VideocamIcon from '@mui/icons-material/Videocam';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useWindows } from '../contexts/WindowContext';
import { useChat } from '../contexts/ChatContext';
import ChatNasPickerDialog from './ChatNasPickerDialog';
import { getWindowLayerZIndex } from './windowLayerPolicy';
import ChatInviteDialog from './ChatInviteDialog';

const WORKSPACE_WINDOW_ID = 'chat_workspace_main';

const formatMessageTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
};

const formatRoomTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('ko-KR', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAttachmentItemIcon = (item = {}) => {
  const ext = String(item.name || '').split('.').pop().toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return <ImageIcon sx={{ fontSize: 18, color: 'info.main' }} />;
  }
  if (item.type === 'folder') {
    return <FolderIcon sx={{ fontSize: 18, color: 'warning.main' }} />;
  }
  return <InsertDriveFileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />;
};

const buildManifestFromFiles = (files = []) => {
  return files.map((file) => ({
    originalName: file.name,
    relativePath: file.webkitRelativePath || file.name,
  }));
};

const readEntryRecursively = async (entry, prefix = '') => {
  if (!entry) return [];

  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    return [{
      file,
      relativePath: `${prefix}${file.name}`,
    }];
  }

  if (entry.isDirectory) {
    const dirName = entry.name || '';
    const reader = entry.createReader();
    const children = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
    let result = [];
    for (const child of children) {
      result = result.concat(await readEntryRecursively(child, `${prefix}${dirName}/`));
    }
    return result;
  }

  return [];
};

const extractDroppedDeviceFiles = async (dataTransfer) => {
  const items = dataTransfer?.items ? Array.from(dataTransfer.items) : [];
  if (items.length > 0 && items.some((item) => item.webkitGetAsEntry && item.webkitGetAsEntry())) {
    let out = [];
    for (const item of items) {
      const entry = item.webkitGetAsEntry && item.webkitGetAsEntry();
      if (!entry) continue;
      out = out.concat(await readEntryRecursively(entry, ''));
    }
    return out;
  }

  const files = Array.from(dataTransfer?.files || []);
  return files.map((file) => ({
    file,
    relativePath: file.webkitRelativePath || file.name,
  }));
};

const toChatMeta = (conversation) => {
  if (!conversation?.conversationId) return null;

  const isDirect = conversation.type === 'direct';
  const other = conversation.otherUser || {};

  if (isDirect) {
    return {
      conversationId: conversation.conversationId,
      conversationType: 'direct',
      title: other.displayName || other.username || '알 수 없는 사용자',
      subtitle:
        other.displayName && other.displayName !== other.username
          ? `@${other.username}`
          : (other.role || '1:1 채팅'),
      avatarText: (other.displayName || other.username || 'D')[0]?.toUpperCase(),
      avatarColor:
        other.role === 'MASTER'
          ? 'error.main'
          : other.role === 'MANAGER'
          ? 'warning.main'
          : 'primary.main',
    };
  }

  const participantCount = Array.isArray(conversation.participantUids)
    ? conversation.participantUids.length
    : 0;

  return {
    conversationId: conversation.conversationId,
    conversationType: 'group',
    title: conversation.title || '그룹 채팅',
    subtitle: participantCount > 0 ? `참여자 ${participantCount}명` : '그룹 채팅',
    avatarText: 'G',
    avatarColor: 'secondary.main',
    ownerUid: conversation.ownerUid || conversation.createdByUid || '',
    coHostUids: conversation.coHostUids || [],
    participantUids: conversation.participantUids || [],
    pendingInviteUids: conversation.pendingInviteUids || [],
    participants: conversation.participants || [],
    pendingInvites: conversation.pendingInvites || [],
    viewerRole: conversation.viewerRole || 'member',
    viewerCanManage: !!conversation.viewerCanManage,
    viewerCanDelete: !!conversation.viewerCanDelete,
  };
};

const getConversationMeetingCode = (conversationId) =>
  `CHAT-${String(conversationId || '').replace(/[^a-zA-Z0-9]/g, '').slice(-24).toUpperCase() || Date.now().toString(36).toUpperCase()}`;

const ChatWorkspaceWindowLayer = () => {
  const theme = useTheme();
  const messageViewportRef = useRef(null);
  const deviceFilesInputRef = useRef(null);
  const deviceFolderInputRef = useRef(null);

  const {
    openWindows,
    setOpenWindows,
    focusedContext,
    focusWindow,
    closeWindow,
    toggleMinimize,
    toggleMaximize,
    openFolderWindowByPath,
    openFileWindowByPath,
    openAppWindow,
  } = useWindows();

  const {
    currentUserUid,
    conversations,
    drafts,
    setDraft,
    messagesByConversation,
    attachmentDraftsByConversation,
    attachmentUploadStateByConversation,
    loadingConversations,
    loadConversations,
    loadMessages,
    sendMessage,
    markConversationRead,
    saveReceivedAttachments,
    createDeviceAttachmentBundle,
    createNasAttachmentBundle,
    removeAttachmentBundle,
    leaveConversation,
    transferConversationOwner,
    setConversationCoHost,
    kickConversationParticipant,
    deleteConversation,
  } = useChat();

  const workspaceWin = useMemo(
    () => openWindows.find((w) => w.id === WORKSPACE_WINDOW_ID && w.winType === 'chat' && w.chatMode === 'workspace') || null,
    [openWindows]
  );

  const [roomQuery, setRoomQuery] = useState('');
  const [attachMenuAnchorEl, setAttachMenuAnchorEl] = useState(null);
  const [roomMenuAnchorEl, setRoomMenuAnchorEl] = useState(null);
  const [nasPickerState, setNasPickerState] = useState({ open: false, conversationId: null });
  const [dragOverConversationId, setDragOverConversationId] = useState(null);
  const [savingMessageIds, setSavingMessageIds] = useState({});
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  useEffect(() => {
    if (!workspaceWin?.id) return;
    loadConversations();
  }, [workspaceWin?.id, loadConversations]);

  useEffect(() => {
    if (!deviceFolderInputRef.current) return;
    deviceFolderInputRef.current.setAttribute('webkitdirectory', '');
    deviceFolderInputRef.current.setAttribute('directory', '');
    deviceFolderInputRef.current.multiple = true;
  }, [workspaceWin]);

  const filteredConversations = useMemo(() => {
    const q = roomQuery.trim().toLowerCase();
    if (!q) return conversations;

    return conversations.filter((conversation) => {
      const other = conversation.otherUser || {};
      const title =
        conversation.type === 'direct'
          ? (other.displayName || other.username || '')
          : (conversation.title || '그룹 채팅');

      return [
        title,
        other.username,
        other.displayName,
        conversation.lastMessagePreview,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [conversations, roomQuery]);

  const selectedConversationId = workspaceWin?.workspaceSelectedConversationId || null;

  const selectedConversation = useMemo(() => {
    if (!selectedConversationId) return null;
    return conversations.find((item) => item.conversationId === selectedConversationId) || null;
  }, [conversations, selectedConversationId]);

  useEffect(() => {
    if (!workspaceWin) return;
    if (selectedConversationId) return;
    if (conversations.length === 0) return;

    setOpenWindows((prev) =>
      prev.map((w) =>
        w.id === WORKSPACE_WINDOW_ID
          ? { ...w, workspaceSelectedConversationId: conversations[0].conversationId }
          : w
      )
    );
  }, [workspaceWin, selectedConversationId, conversations, setOpenWindows]);

  useEffect(() => {
    if (!selectedConversationId) return;
    loadMessages(selectedConversationId, { force: true });
  }, [selectedConversationId, loadMessages]);

  const messages = useMemo(() => {
    if (!selectedConversationId) return [];
    return messagesByConversation[selectedConversationId] || [];
  }, [messagesByConversation, selectedConversationId]);

  const attachmentDrafts = useMemo(() => {
    if (!selectedConversationId) return [];
    return attachmentDraftsByConversation[selectedConversationId] || [];
  }, [attachmentDraftsByConversation, selectedConversationId]);

  const attachmentUploads = useMemo(() => {
    if (!selectedConversationId) return [];
    return attachmentUploadStateByConversation[selectedConversationId] || [];
  }, [attachmentUploadStateByConversation, selectedConversationId]);

  const hasUnreadFromOther = useMemo(() => {
    return messages.some((msg) =>
      msg.senderUid !== currentUserUid &&
      !msg.deleted &&
      !(Array.isArray(msg.readByUids) ? msg.readByUids : []).includes(currentUserUid)
    );
  }, [messages, currentUserUid]);

  useEffect(() => {
    if (!selectedConversationId || !hasUnreadFromOther) return;
    markConversationRead(selectedConversationId);
  }, [selectedConversationId, hasUnreadFromOther, markConversationRead]);

  useEffect(() => {
    if (!selectedConversationId) return;
    const el = messageViewportRef.current;
    if (!el) return;

    const moveToBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    requestAnimationFrame(moveToBottom);
    const timer = setTimeout(moveToBottom, 0);
    return () => clearTimeout(timer);
  }, [selectedConversationId, messages, attachmentDrafts, attachmentUploads]);

  if (!workspaceWin) return null;

  const currentMeta = toChatMeta(selectedConversation);
  const isGroupSelected = selectedConversation?.type === 'group';
  const selectedParticipants = Array.isArray(selectedConversation?.participants) ? selectedConversation.participants : [];
  const selectedOwnerUid = selectedConversation?.ownerUid || selectedConversation?.createdByUid || '';
  const selectedCoHostUids = Array.isArray(selectedConversation?.coHostUids) ? selectedConversation.coHostUids : [];
  const selectedViewerRole = selectedConversation?.viewerRole || 'member';
  const selectedCanManage = !!selectedConversation?.viewerCanManage || selectedViewerRole === 'owner' || selectedViewerRole === 'cohost';
  const selectedCanDelete = !!selectedConversation?.viewerCanDelete || selectedViewerRole === 'owner';

  const closeRoomMenu = () => setRoomMenuAnchorEl(null);

  const handleRoomActionResult = async (conversation) => {
    await loadConversations({ silent: true });
    if (!conversation?.conversationId || conversation.deletedAt || conversation.viewerRole === 'none') {
      const nextConversation = conversations.find((item) => item.conversationId !== selectedConversationId) || null;
      handleSelectConversation(nextConversation?.conversationId || null);
      return;
    }
    handleSelectConversation(conversation.conversationId);
  };

  const handleLeaveRoom = async () => {
    if (!selectedConversationId) return;
    if (!window.confirm('이 채팅방에서 나가시겠습니까?')) return;
    try {
      const conversation = await leaveConversation(selectedConversationId);
      closeRoomMenu();
      await handleRoomActionResult(conversation);
    } catch (err) {
      alert(err.response?.data?.error || '채팅방에서 나가지 못했습니다.');
    }
  };

  const handleDeleteRoom = async () => {
    if (!selectedConversationId) return;
    if (!window.confirm('채팅방을 파기하면 모든 참가자의 목록에서 사라집니다. 계속할까요?')) return;
    if (window.prompt('정말 파기하려면 "방 파기"를 입력하세요.') !== '방 파기') return;
    try {
      const conversation = await deleteConversation(selectedConversationId);
      closeRoomMenu();
      await handleRoomActionResult(conversation);
    } catch (err) {
      alert(err.response?.data?.error || '채팅방을 파기하지 못했습니다.');
    }
  };

  const handleTransferOwner = async (member) => {
    if (!selectedConversationId || !member?.userUid) return;
    if (!window.confirm(`${member.displayName || member.username}님에게 방장을 위임할까요?`)) return;
    try {
      const conversation = await transferConversationOwner(selectedConversationId, member.userUid);
      await handleRoomActionResult(conversation);
    } catch (err) {
      alert(err.response?.data?.error || '방장 위임에 실패했습니다.');
    }
  };

  const handleToggleCoHost = async (member) => {
    if (!selectedConversationId || !member?.userUid) return;
    const enabled = !selectedCoHostUids.includes(member.userUid);
    try {
      const conversation = await setConversationCoHost(selectedConversationId, member.userUid, enabled);
      await handleRoomActionResult(conversation);
    } catch (err) {
      alert(err.response?.data?.error || '부방장 설정에 실패했습니다.');
    }
  };

  const handleKickMember = async (member) => {
    if (!selectedConversationId || !member?.userUid) return;
    if (!window.confirm(`${member.displayName || member.username}님을 채팅방에서 내보낼까요?`)) return;
    try {
      const conversation = await kickConversationParticipant(selectedConversationId, member.userUid);
      await handleRoomActionResult(conversation);
    } catch (err) {
      alert(err.response?.data?.error || '내보내기에 실패했습니다.');
    }
  };

  const handleSelectConversation = (conversationId) => {
    setOpenWindows((prev) =>
      prev.map((w) =>
        w.id === WORKSPACE_WINDOW_ID
          ? { ...w, workspaceSelectedConversationId: conversationId }
          : w
      )
    );
  };

  const handleOpenMeeting = () => {
    if (!selectedConversationId) return;
    openAppWindow({
      id: 'meeting',
      title: '화상회의',
      width: 920,
      height: 640,
      payload: {
        roomCode: getConversationMeetingCode(selectedConversationId),
        autoJoin: false,
        conversationId: selectedConversationId,
      },
    });
  };

  const handleInviteComplete = (conversation) => {
    if (!conversation?.conversationId) return;
    handleSelectConversation(conversation.conversationId);
  };

  const handleSend = async () => {
    if (!selectedConversationId) return;

    const value = drafts[selectedConversationId] || '';
    if (!String(value).trim() && attachmentDrafts.length === 0) return;

    try {
      await sendMessage(selectedConversationId, { text: value });
      setDraft(selectedConversationId, '');
    } catch (err) {
      console.error('메시지 전송 실패', err);
      alert(err.response?.data?.error || '메시지 전송에 실패했습니다.');
    }
  };

  const handleDeviceInput = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!selectedConversationId || files.length === 0) return;

    try {
      await createDeviceAttachmentBundle(selectedConversationId, {
        files,
        manifest: buildManifestFromFiles(files),
      });
    } catch (err) {
      alert(err.response?.data?.error || '디바이스 첨부 추가에 실패했습니다.');
    }
  };

  const handleNasConfirm = async (paths) => {
    const conversationId = nasPickerState.conversationId;
    if (!conversationId) return;
    try {
      await createNasAttachmentBundle(conversationId, paths);
      setNasPickerState({ open: false, conversationId: null });
    } catch (err) {
      alert(err.response?.data?.error || 'NAS 첨부 추가에 실패했습니다.');
    }
  };

  const handleWorkspaceDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverConversationId(null);

    if (!selectedConversationId) return;

    try {
      const appDragData = e.dataTransfer.getData('application/json');
      if (appDragData) {
        const parsed = JSON.parse(appDragData);
        const draggedPaths = Array.isArray(parsed.draggedPaths) ? parsed.draggedPaths : [];
        if (draggedPaths.length > 0) {
          await createNasAttachmentBundle(selectedConversationId, draggedPaths);
          return;
        }
      }
    } catch (err) {
      console.warn('워크스페이스 내부 드래그 데이터 파싱 실패', err);
    }

    try {
      const dropped = await extractDroppedDeviceFiles(e.dataTransfer);
      if (dropped.length === 0) return;

      await createDeviceAttachmentBundle(selectedConversationId, {
        files: dropped.map((item) => item.file),
        manifest: dropped.map((item) => ({
          originalName: item.file.name,
          relativePath: item.relativePath,
        })),
      });
    } catch (err) {
      alert(err.response?.data?.error || '드롭 첨부 추가에 실패했습니다.');
    }
  };

  const getMessageAction = (message) => {
    const entryCount = Array.isArray(message.viewerSavedEntries) ? message.viewerSavedEntries.length : 0;

    if (message.viewerSavedState === 'ready') {
      return { mode: 'open', label: entryCount > 1 ? '받은 파일 열기' : '열기' };
    }
    if (message.viewerSavedState === 'missing') {
      return { mode: 'redownload', label: '다시 다운로드' };
    }
    return { mode: 'download', label: '받은 파일에 저장' };
  };

  const handleOpenSavedTarget = async (message) => {
    const target = message?.viewerOpenTarget;
    if (!target?.relativePath) return;

    if (target.type === 'folder') {
      openFolderWindowByPath(target.relativePath, target.name || '받은 파일');
      return;
    }

    await openFileWindowByPath(target.relativePath, target.name || null, false);
  };

  const handleSaveReceived = async (messageId) => {
    if (!selectedConversationId || !messageId) return;
    setSavingMessageIds((prev) => ({ ...prev, [messageId]: true }));
    try {
      const result = await saveReceivedAttachments(selectedConversationId, messageId);
      const count = Array.isArray(result?.savedEntries) ? result.savedEntries.length : 0;
      if (!result?.alreadySaved) {
        alert(`받은 파일에 ${count}개 항목을 저장했습니다.`);
      }
    } catch (err) {
      alert(err.response?.data?.error || '받은 파일 저장에 실패했습니다.');
    } finally {
      setSavingMessageIds((prev) => ({ ...prev, [messageId]: false }));
    }
  };

  const renderAttachmentUploads = () => {
    if (attachmentUploads.length === 0) return null;

    return (
      <Box sx={{ px: 1.25, pt: 1.1, pb: 0.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {attachmentUploads.map((upload) => (
          <Paper
            key={upload.uploadId}
            variant="outlined"
            sx={{ p: 1, borderRadius: 2, backgroundColor: 'background.default' }}
          >
            <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
              {upload.sourceType === 'nas' ? 'NAS 첨부 준비 중' : '내 PC 업로드 중'} · {upload.totalCount || 0}개
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4 }}>
              {upload.statusText || '처리 중'}
              {typeof upload.progress === 'number' ? ` (${upload.progress}%)` : ''}
            </Typography>
            <LinearProgress
              sx={{ mt: 0.9 }}
              variant={typeof upload.progress === 'number' ? 'determinate' : 'indeterminate'}
              value={typeof upload.progress === 'number' ? upload.progress : undefined}
            />
          </Paper>
        ))}
      </Box>
    );
  };

  const renderAttachmentDrafts = () => {
    if (attachmentDrafts.length === 0) return null;

    return (
      <Box sx={{ px: 1.25, pt: 1.1, pb: 0.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {attachmentDrafts.map((bundle) => (
          <Paper
            key={bundle.bundleId}
            variant="outlined"
            sx={{ p: 1, borderRadius: 2, backgroundColor: 'background.default' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, display: 'block' }}>
                  {bundle.sourceType === 'nas' ? 'NAS 첨부' : '내 PC 첨부'} · {bundle.itemCount || 0}개
                </Typography>
                <Box sx={{ mt: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {(bundle.items || []).map((item, idx) => (
                    <Chip
                      key={`${bundle.bundleId}_${idx}`}
                      size="small"
                      icon={item.type === 'folder' ? <FolderIcon /> : <InsertDriveFileIcon />}
                      label={item.relativePath || item.name}
                      sx={{ maxWidth: 320 }}
                    />
                  ))}
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => removeAttachmentBundle(selectedConversationId, bundle.bundleId)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  const renderMessageAttachments = (message) => {
    const items = (message.attachments || []).flatMap((bundle) => bundle.items || []);
    if (items.length === 0) return null;

    return (
      <Box sx={{ mt: message.text ? 0.75 : 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {items.map((item, idx) => (
          <Box
            key={`${message.messageId}_${idx}`}
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              backgroundColor: 'rgba(255,255,255,0.12)',
            }}
          >
            {getAttachmentItemIcon(item)}
            <Typography
              variant="caption"
              sx={{
                userSelect: 'text',
                WebkitUserSelect: 'text',
                wordBreak: 'break-all',
              }}
            >
              {item.relativePath || item.name}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const fillsParent = !!workspaceWin.isMaximized;
  const isActive = focusedContext === workspaceWin.id;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: getWindowLayerZIndex([workspaceWin], focusedContext),
      }}
    >
      <input
        ref={deviceFilesInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          handleDeviceInput(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={deviceFolderInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => {
          handleDeviceInput(e.target.files);
          e.target.value = '';
        }}
      />

      <Box sx={{ display: workspaceWin.isMinimized ? 'none' : 'block', pointerEvents: 'auto' }}>
        <Rnd
          size={fillsParent ? { width: '100vw', height: 'calc(var(--app-viewport-height) - 48px)' } : { width: workspaceWin.width, height: workspaceWin.height }}
          position={fillsParent ? { x: 0, y: 48 } : { x: workspaceWin.x, y: workspaceWin.y }}
          onMouseDown={(e) => {
            e.stopPropagation();
            focusWindow(workspaceWin.id);
          }}
          dragHandleClassName="chat-workspace-header"
          cancel=".MuiIconButton-root,.MuiButton-root,.MuiInputBase-root,button,input,textarea"
          enableUserSelectHack={true}
          bounds="window"
          disableDragging={fillsParent}
          enableResizing={!fillsParent}
          onDragStart={() => {
            document.body.style.userSelect = 'none';
            document.body.style.webkitUserSelect = 'none';
          }}
          onDrag={(e, d) => {
            setOpenWindows((prev) =>
              prev.map((w) => (w.id === workspaceWin.id ? { ...w, x: d.x, y: d.y } : w))
            );
          }}
          onDragStop={(e, d) => {
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            setOpenWindows((prev) =>
              prev.map((w) => (w.id === workspaceWin.id ? { ...w, x: d.x, y: d.y } : w))
            );
          }}
          onResizeStop={(e, direction, ref, delta, position) => {
            setOpenWindows((prev) =>
              prev.map((w) =>
                w.id === workspaceWin.id
                  ? {
                      ...w,
                      width: ref.style.width,
                      height: ref.style.height,
                      x: position.x,
                      y: position.y,
                    }
                  : w
              )
            );
          }}
          style={{ zIndex: fillsParent ? 99998 : workspaceWin.zIndex }}
        >
          <Paper
            elevation={isActive ? 12 : 4}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
              border: isActive ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
              borderRadius: fillsParent ? 0 : 2,
              bgcolor: 'background.paper',
            }}
          >
            <Box
              className="chat-workspace-header"
              sx={{
                px: 1.25,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f8fafc',
                borderBottom: `1px solid ${theme.palette.divider}`,
                cursor: fillsParent ? 'default' : 'move',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1 }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main', fontSize: '0.85rem', fontWeight: 'bold' }}>
                  C
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                    채팅 관리
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    채팅방 목록과 대화를 한 창에서 관리합니다.
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleMinimize(workspaceWin.id); }} title="최소화">
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleMaximize(workspaceWin.id); }} title="최대화">
                  <CropSquareIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); closeWindow(workspaceWin.id); }} title="닫기">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
              <Box
                sx={{
                  width: 320,
                  borderRight: `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  bgcolor: 'background.default',
                }}
              >
                <Box sx={{ p: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="채팅방 검색"
                    value={roomQuery}
                    onChange={(e) => setRoomQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1 }}>
                  {loadingConversations ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                      채팅방 목록을 불러오는 중...
                    </Typography>
                  ) : filteredConversations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 2 }}>
                      표시할 채팅방이 없습니다.
                    </Typography>
                  ) : (
                    <List disablePadding>
                      {filteredConversations.map((conversation) => {
                        const isDirect = conversation.type === 'direct';
                        const other = conversation.otherUser || {};
                        const title = isDirect
                          ? (other.displayName || other.username || '알 수 없는 사용자')
                          : (conversation.title || '그룹 채팅');
                        const selected = selectedConversationId === conversation.conversationId;
                        const unreadCount = Number(conversation.unreadCount) || 0;

                        return (
                          <ListItemButton
                            key={conversation.conversationId}
                            onClick={() => handleSelectConversation(conversation.conversationId)}
                            sx={{
                              borderRadius: 2,
                              mb: 0.5,
                              alignItems: 'flex-start',
                              backgroundColor: selected ? 'action.selected' : 'transparent',
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  bgcolor: isDirect ? 'primary.main' : 'secondary.main',
                                  fontWeight: 'bold',
                                }}
                              >
                                {isDirect ? (title?.[0] || 'D').toUpperCase() : <GroupIcon sx={{ fontSize: 20 }} />}
                              </Avatar>
                            </ListItemAvatar>

                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flexWrap: 'wrap' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                    {title}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    color={isDirect ? 'primary' : 'secondary'}
                                    variant="outlined"
                                    icon={isDirect ? <PersonIcon /> : <GroupIcon />}
                                    label={isDirect ? '1:1' : '그룹'}
                                    sx={{ height: 20 }}
                                  />
                                  {unreadCount > 0 && (
                                    <Chip
                                      size="small"
                                      color="error"
                                      label={unreadCount > 99 ? '99+' : unreadCount}
                                      sx={{ height: 20 }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.4 }}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                      display: 'block',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: 190,
                                    }}
                                  >
                                    {conversation.lastMessagePreview || '아직 메시지가 없습니다.'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                    {formatRoomTime(conversation.lastMessageAt || conversation.updatedAt || conversation.createdAt)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Box>

              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {selectedConversationId && currentMeta ? (
                  <>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: theme.palette.mode === 'dark' ? '#111827' : '#f8fafc',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Avatar
                          sx={{
                            width: 30,
                            height: 30,
                            bgcolor: currentMeta.avatarColor,
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                          }}
                        >
                          {currentMeta.avatarText}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                            {currentMeta.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {currentMeta.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        <IconButton size="small" onClick={handleOpenMeeting} title="화상회의 시작">
                          <VideocamIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setInviteDialogOpen(true)} title="인원 추가">
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                        {isGroupSelected && (
                          <IconButton size="small" onClick={(event) => setRoomMenuAnchorEl(event.currentTarget)} title="채팅방 관리">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    <Box
                      ref={messageViewportRef}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        p: 1.5,
                        bgcolor:
                          dragOverConversationId === selectedConversationId
                            ? 'action.hover'
                            : 'background.default',
                        border:
                          dragOverConversationId === selectedConversationId
                            ? `2px dashed ${theme.palette.primary.main}`
                            : '2px solid transparent',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                        userSelect: 'text',
                        WebkitUserSelect: 'text',
                        overscrollBehavior: 'contain',
                      }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOverConversationId(selectedConversationId);
                      }}
                      onDragLeave={(e) => {
                        e.stopPropagation();
                        setDragOverConversationId((prev) =>
                          prev === selectedConversationId ? null : prev
                        );
                      }}
                      onDrop={handleWorkspaceDrop}
                    >
                      {messages.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          아직 대화가 없습니다.
                        </Typography>
                      ) : messages.map((msg) => {
                        const isMine = msg.senderUid === currentUserUid || msg.sender === 'me';
                        const key = msg.messageId || msg.id || `${selectedConversationId}_${msg.createdAt}`;
                        const hasAttachments = Array.isArray(msg.attachments) && msg.attachments.length > 0;
                        const messageAction = getMessageAction(msg);

                        return (
                          <Box
                            key={key}
                            sx={{
                              display: 'flex',
                              justifyContent: isMine ? 'flex-end' : 'flex-start',
                              mb: 1,
                            }}
                            onDragStart={(e) => e.preventDefault()}
                          >
                            <Box
                              sx={{
                                maxWidth: '78%',
                                px: 1.25,
                                py: 1,
                                borderRadius: 2,
                                bgcolor: isMine ? 'primary.main' : 'background.paper',
                                color: isMine ? 'primary.contrastText' : 'text.primary',
                                border: isMine ? 'none' : `1px solid ${theme.palette.divider}`,
                                boxShadow: 1,
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                              }}
                            >
                              {!!msg.text && (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    userSelect: 'text',
                                    WebkitUserSelect: 'text',
                                    cursor: 'text',
                                  }}
                                >
                                  {msg.text}
                                </Typography>
                              )}

                              {renderMessageAttachments(msg)}

                              {hasAttachments && (
                                <Box sx={{ mt: 0.9, display: 'flex', justifyContent: 'flex-end' }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => messageAction.mode === 'open'
                                      ? handleOpenSavedTarget(msg)
                                      : handleSaveReceived(msg.messageId)}
                                    disabled={messageAction.mode !== 'open' && !!savingMessageIds[msg.messageId]}
                                  >
                                    {messageAction.mode !== 'open' && savingMessageIds[msg.messageId]
                                      ? '저장 중...'
                                      : messageAction.label}
                                  </Button>
                                </Box>
                              )}

                              <Typography
                                variant="caption"
                                sx={{
                                  mt: 0.5,
                                  display: 'block',
                                  textAlign: 'right',
                                  color: isMine ? 'rgba(255,255,255,0.82)' : 'text.secondary',
                                  userSelect: 'none',
                                  WebkitUserSelect: 'none',
                                }}
                              >
                                {formatMessageTime(msg.createdAt)}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {renderAttachmentUploads()}
                    {renderAttachmentDrafts()}

                    <Divider />

                    <Box sx={{ p: 1.25, pb: 'calc(10px + var(--app-safe-bottom))', display: 'flex', gap: 1, alignItems: 'flex-end', bgcolor: 'background.paper', flexShrink: 0 }}>
                      <IconButton onClick={(e) => setAttachMenuAnchorEl(e.currentTarget)}>
                        <AddIcon />
                      </IconButton>

                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        maxRows={4}
                        placeholder={attachmentDrafts.length > 0 ? '첨부와 함께 보낼 메시지를 입력하세요' : '메시지를 입력하세요'}
                        value={drafts[selectedConversationId] || ''}
                        onChange={(e) => setDraft(selectedConversationId, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={handleSend}
                        sx={{ minWidth: 96, height: 40 }}
                        endIcon={<SendIcon />}
                      >
                        전송
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <ChatBubbleOutlineIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        채팅방을 선택하세요
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        왼쪽 목록에서 direct 또는 그룹 채팅방을 선택하면 이 영역에서 바로 대화할 수 있습니다.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Rnd>
      </Box>

      <Menu
        anchorEl={attachMenuAnchorEl}
        open={Boolean(attachMenuAnchorEl)}
        onClose={() => setAttachMenuAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAttachMenuAnchorEl(null);
            deviceFilesInputRef.current?.click();
          }}
        >
          내 PC 파일 선택
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAttachMenuAnchorEl(null);
            deviceFolderInputRef.current?.click();
          }}
        >
          내 PC 폴더 선택
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAttachMenuAnchorEl(null);
            if (selectedConversationId) {
              setNasPickerState({ open: true, conversationId: selectedConversationId });
            }
          }}
        >
          NAS에서 선택
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={roomMenuAnchorEl}
        open={Boolean(roomMenuAnchorEl)}
        onClose={closeRoomMenu}
        PaperProps={{ sx: { width: 340, maxWidth: 'calc(100vw - 24px)' } }}
      >
        <Box sx={{ px: 1.5, py: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>채팅방 관리</Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedViewerRole === 'owner' ? '방장' : selectedViewerRole === 'cohost' ? '부방장' : '멤버'}
          </Typography>
        </Box>
        <Divider />
        {selectedParticipants.map((member) => {
          const isOwner = member.userUid === selectedOwnerUid;
          const isCoHost = selectedCoHostUids.includes(member.userUid);
          const isMe = member.userUid === currentUserUid;
          return (
            <Box key={member.userUid} sx={{ px: 1.25, py: 0.9 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Avatar sx={{ width: 26, height: 26, fontSize: 12 }}>
                  {(member.displayName || member.username || '?').slice(0, 1)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>
                    {member.displayName || member.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isOwner ? '방장' : isCoHost ? '부방장' : '멤버'}{isMe ? ' · 나' : ''}
                  </Typography>
                </Box>
              </Box>
              {!isMe && selectedCanManage && !isOwner && (
                <Box sx={{ mt: 0.75, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {selectedCanDelete && (
                    <>
                      <Button size="small" variant="text" onClick={() => handleTransferOwner(member)}>
                        방장위임
                      </Button>
                      <Button size="small" variant="text" onClick={() => handleToggleCoHost(member)}>
                        {isCoHost ? '부방장 해제' : '부방장'}
                      </Button>
                    </>
                  )}
                  <Button size="small" color="error" variant="text" onClick={() => handleKickMember(member)}>
                    내보내기
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
        <Divider />
        <MenuItem onClick={handleLeaveRoom}>채팅방 나가기</MenuItem>
        {selectedCanDelete && (
          <MenuItem onClick={handleDeleteRoom} sx={{ color: 'error.main', fontWeight: 900 }}>
            채팅방 파기
          </MenuItem>
        )}
      </Menu>

      <ChatNasPickerDialog
        open={nasPickerState.open}
        onClose={() => setNasPickerState({ open: false, conversationId: null })}
        onConfirm={handleNasConfirm}
      />
      <ChatInviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        conversation={selectedConversation}
        directUserUid={selectedConversation?.otherUser?.userUid || null}
        defaultTitle={`${currentMeta?.title || '채팅'} 그룹`}
        onComplete={handleInviteComplete}
      />
    </Box>
  );
};

export default ChatWorkspaceWindowLayer;
